/**
 * dLive store paths (OSC-style, see device-state-store.ts).
 *
 *   /model, /midiChannel, /currentScene
 *   /<type>/<i>/name | colour | fader | mute | mainAssign
 *   /<type>/<i>/hpf/on | hpf/frequency
 *   /input/<i>/source          "port2:63", "none" (I/O patch, Input A)
 *   /input/<i>/trim | polarity  input trim (dB) and polarity (AHNet)
 *   /input/<i>/stereo          half of a stereo pair (AHNet, read only)
 *   /input/<i>/stereoMode      stereo image mode, see DLIVE_STEREO_MODES (AHNet)
 *   /<type>/<i>/peq/<band>/type | frequency | width | gain
 *   /<type>/<i>/sends/<destType>/<j>/level | on
 *   /preamps/<bank>/<i>/gain | pad | phantom
 *   /ioPorts/<p>/name          I/O port name from Director (AHNet, read only)
 *
 * Write only (the protocol cannot read them back):
 *   /<type>/<i>/dca/<d>        assign to DCA d (0-23)
 *   /<type>/<i>/muteGroup/<g>  assign to mute group g (0-7)
 *   /ufx/key, /ufx/scale       global UFX key / scale
 *   /resync                    re-read everything from the console
 *
 * <type> is a DLiveChannelType, indexes are 0-based. /currentScene is the
 * last scene recalled on the console (read only; the bridge never recalls).
 */
import { DLIVE_CHANNEL_TYPES, DLIVE_COLOURS, DLIVE_PEQ_TYPES, DLIVE_PREAMP_BANKS, DLIVE_STEREO_MODES, isDLiveChannelType, } from "./dlive.js";
// ─── Path builders ───────────────────────────────────────────
export const dliveChannelPath = (type, index) => `/${type}/${index}`;
const CHANNEL_PARAM_SUFFIX = {
    name: "name",
    colour: "colour",
    fader: "fader",
    mute: "mute",
    mainAssign: "mainAssign",
    hpfOn: "hpf/on",
    hpfFrequency: "hpf/frequency",
    source: "source",
    trim: "trim",
    polarity: "polarity",
    stereo: "stereo",
    stereoMode: "stereoMode",
};
export function dliveParamPath(type, index, param) {
    return `${dliveChannelPath(type, index)}/${CHANNEL_PARAM_SUFFIX[param]}`;
}
export function dlivePeqPath(type, index, band, param) {
    return `${dliveChannelPath(type, index)}/peq/${band}/${param}`;
}
export function dliveSendPath(type, index, destType, destIndex, param) {
    return `${dliveChannelPath(type, index)}/sends/${destType}/${destIndex}/${param}`;
}
export function dliveAssignPath(type, index, group, number) {
    return `${dliveChannelPath(type, index)}/${group}/${number}`;
}
/** `port`: 1-based I/O port number */
export const dliveIoPortNamePath = (port) => `/ioPorts/${port}/name`;
export function dlivePreampPath(bank, index, param) {
    return `/preamps/${bank}/${index}/${param}`;
}
// ─── Path parser ─────────────────────────────────────────────
const CHANNEL_PARAMS = new Map(Object.entries(CHANNEL_PARAM_SUFFIX).map(([param, suffix]) => [
    suffix,
    param,
]));
const PEQ_PARAMS = new Set(["type", "frequency", "width", "gain"]);
const PREAMP_PARAMS = new Set(["gain", "pad", "phantom"]);
const INDEX = /^\d+$/;
/** Parse a dLive store path. Indexes are not range checked. */
export function parseDLivePath(path) {
    switch (path) {
        case "/model":
            return { kind: "model" };
        case "/midiChannel":
            return { kind: "midiChannel" };
        case "/currentScene":
            return { kind: "scene" };
        case "/ufx/key":
            return { kind: "ufx", param: "key" };
        case "/ufx/scale":
            return { kind: "ufx", param: "scale" };
        case "/resync":
            return { kind: "resync" };
    }
    const parts = path.split("/").slice(1);
    if (parts[0] === "ioPorts") {
        if (parts.length === 3 && INDEX.test(parts[1]) && parts[2] === "name") {
            return { kind: "ioPortName", port: Number(parts[1]) };
        }
        return null;
    }
    if (parts[0] === "preamps") {
        // /preamps/<bank>/<i>/<param>
        if (parts.length === 4 &&
            DLIVE_PREAMP_BANKS.includes(parts[1]) &&
            INDEX.test(parts[2]) &&
            PREAMP_PARAMS.has(parts[3])) {
            return {
                kind: "preamp",
                bank: parts[1],
                index: Number(parts[2]),
                param: parts[3],
            };
        }
        return null;
    }
    if (parts.length < 3 || !INDEX.test(parts[1]))
        return null;
    const index = Number(parts[1]);
    if (!isDLiveChannelType(parts[0]))
        return null;
    const type = parts[0];
    const rest = parts.slice(2);
    const param = CHANNEL_PARAMS.get(rest.join("/"));
    if (param)
        return { kind: "channel", type, index, param };
    if (rest.length === 3 &&
        rest[0] === "peq" &&
        INDEX.test(rest[1]) &&
        PEQ_PARAMS.has(rest[2])) {
        return {
            kind: "peq",
            type,
            index,
            band: Number(rest[1]),
            param: rest[2],
        };
    }
    if (rest.length === 4 &&
        rest[0] === "sends" &&
        isDLiveChannelType(rest[1]) &&
        INDEX.test(rest[2]) &&
        (rest[3] === "level" || rest[3] === "on")) {
        return {
            kind: "send",
            type,
            index,
            destType: rest[1],
            destIndex: Number(rest[2]),
            param: rest[3],
        };
    }
    if (rest.length === 2 &&
        (rest[0] === "dca" || rest[0] === "muteGroup") &&
        INDEX.test(rest[1])) {
        return {
            kind: "assign",
            type,
            index,
            group: rest[0],
            number: Number(rest[1]),
        };
    }
    return null;
}
// ─── Conversion ──────────────────────────────────────────────
export function dLiveToStore(s) {
    const values = {};
    values["/model"] = s.model;
    values["/midiChannel"] = s.midiChannel;
    values["/currentScene"] = s.currentScene;
    const set = (path, value) => {
        if (value !== undefined)
            values[path] = value;
    };
    for (const ch of s.channels) {
        const { type, index } = ch;
        set(dliveParamPath(type, index, "name"), ch.name);
        set(dliveParamPath(type, index, "colour"), ch.colour);
        set(dliveParamPath(type, index, "fader"), ch.fader);
        set(dliveParamPath(type, index, "mute"), ch.mute);
        set(dliveParamPath(type, index, "mainAssign"), ch.mainAssign);
        set(dliveParamPath(type, index, "hpfOn"), ch.hpfOn);
        set(dliveParamPath(type, index, "hpfFrequency"), ch.hpfFrequency);
        set(dliveParamPath(type, index, "source"), ch.source);
        set(dliveParamPath(type, index, "trim"), ch.trim);
        set(dliveParamPath(type, index, "polarity"), ch.polarity);
        set(dliveParamPath(type, index, "stereo"), ch.stereo);
        set(dliveParamPath(type, index, "stereoMode"), ch.stereoMode);
        for (const band of ch.peq ?? []) {
            set(dlivePeqPath(type, index, band.band, "type"), band.type);
            set(dlivePeqPath(type, index, band.band, "frequency"), band.frequency);
            set(dlivePeqPath(type, index, band.band, "width"), band.width);
            set(dlivePeqPath(type, index, band.band, "gain"), band.gain);
        }
        for (const send of ch.sends ?? []) {
            const { destType, destIndex } = send;
            set(dliveSendPath(type, index, destType, destIndex, "level"), send.level);
            set(dliveSendPath(type, index, destType, destIndex, "on"), send.on);
        }
    }
    for (const p of s.preamps) {
        set(dlivePreampPath(p.bank, p.index, "gain"), p.gain);
        set(dlivePreampPath(p.bank, p.index, "pad"), p.pad);
        set(dlivePreampPath(p.bank, p.index, "phantom"), p.phantom);
    }
    for (const [port, name] of Object.entries(s.ioPortNames ?? {})) {
        set(dliveIoPortNamePath(Number(port)), name);
    }
    return values;
}
const num = (v) => v === undefined || v === "" ? undefined : Number(v);
const bool = (v) => v === undefined ? undefined : v === true || v === "true" || v === 1;
function enumValue(v, options) {
    return typeof v === "string" && options.includes(v)
        ? v
        : undefined;
}
const TYPE_ORDER = new Map(DLIVE_CHANNEL_TYPES.map((t, i) => [t, i]));
export function storeToDLiveState(values) {
    const channels = new Map();
    const peqs = new Map();
    const sends = new Map();
    const preamps = new Map();
    const ioPortNames = {};
    const channelOf = (type, index) => {
        const key = `${type}:${index}`;
        let ch = channels.get(key);
        if (!ch) {
            ch = { type, index };
            channels.set(key, ch);
        }
        return { ch, key };
    };
    for (const [path, value] of Object.entries(values)) {
        const target = parseDLivePath(path);
        if (!target)
            continue;
        switch (target.kind) {
            case "channel": {
                const { ch } = channelOf(target.type, target.index);
                switch (target.param) {
                    case "name":
                        ch.name = String(value);
                        break;
                    case "colour":
                        ch.colour = enumValue(value, DLIVE_COLOURS);
                        break;
                    case "fader":
                        ch.fader = num(value);
                        break;
                    case "mute":
                        ch.mute = bool(value);
                        break;
                    case "mainAssign":
                        ch.mainAssign = bool(value);
                        break;
                    case "hpfOn":
                        ch.hpfOn = bool(value);
                        break;
                    case "hpfFrequency":
                        ch.hpfFrequency = num(value);
                        break;
                    case "source":
                        ch.source = String(value);
                        break;
                    case "trim":
                        ch.trim = num(value);
                        break;
                    case "polarity":
                        ch.polarity = bool(value);
                        break;
                    case "stereo":
                        ch.stereo = bool(value);
                        break;
                    case "stereoMode":
                        ch.stereoMode = enumValue(value, DLIVE_STEREO_MODES);
                        break;
                }
                break;
            }
            case "peq": {
                const { key } = channelOf(target.type, target.index);
                let bands = peqs.get(key);
                if (!bands)
                    peqs.set(key, (bands = new Map()));
                let band = bands.get(target.band);
                if (!band)
                    bands.set(target.band, (band = { band: target.band }));
                if (target.param === "type")
                    band.type = enumValue(value, DLIVE_PEQ_TYPES);
                else
                    band[target.param] = num(value);
                break;
            }
            case "send": {
                const { key } = channelOf(target.type, target.index);
                let list = sends.get(key);
                if (!list)
                    sends.set(key, (list = new Map()));
                const sendKey = `${target.destType}:${target.destIndex}`;
                let send = list.get(sendKey);
                if (!send)
                    list.set(sendKey, (send = {
                        destType: target.destType,
                        destIndex: target.destIndex,
                    }));
                if (target.param === "level")
                    send.level = num(value);
                else
                    send.on = bool(value);
                break;
            }
            case "preamp": {
                const key = `${target.bank}:${target.index}`;
                let p = preamps.get(key);
                if (!p)
                    preamps.set(key, (p = { bank: target.bank, index: target.index }));
                if (target.param === "gain")
                    p.gain = num(value);
                else
                    p[target.param] = bool(value);
                break;
            }
            case "ioPortName":
                ioPortNames[target.port] = String(value);
                break;
            default:
                break;
        }
    }
    for (const [key, bands] of peqs) {
        const ch = channels.get(key);
        ch.peq = [...bands.values()].sort((a, b) => a.band - b.band);
    }
    for (const [key, list] of sends) {
        const ch = channels.get(key);
        ch.sends = [...list.values()].sort((a, b) => TYPE_ORDER.get(a.destType) - TYPE_ORDER.get(b.destType) ||
            a.destIndex - b.destIndex);
    }
    return {
        model: String(values["/model"] ?? "dLive"),
        midiChannel: Number(values["/midiChannel"] ?? 1),
        currentScene: Number(values["/currentScene"] ?? 0),
        channels: [...channels.values()].sort((a, b) => TYPE_ORDER.get(a.type) - TYPE_ORDER.get(b.type) || a.index - b.index),
        preamps: [...preamps.values()].sort((a, b) => DLIVE_PREAMP_BANKS.indexOf(a.bank) -
            DLIVE_PREAMP_BANKS.indexOf(b.bank) || a.index - b.index),
        ioPortNames,
    };
}
